import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import RecipientPicker from './RecipientPicker';
import CircularProgress from '@mui/material/CircularProgress';
import { ArrowLeft, Check, Users } from 'lucide-react';

// Presentational: layout only, all state lives in GroupManagerContainer.
// Deliberately mirrors GroupComposer — same header, same name field, same
// RecipientPicker — so editing a group reads as the same surface as creating
// one. The only structural difference is the save button, which stays
// disabled until something actually changed.
const GroupManager = ({
  onBack,
  name,
  onNameChange,
  students,
  candidatesLoading,
  selectedIds,
  onToggleMember,
  onSelectAll,
  onClearAll,
  maxMembers,
  saving,
  error,
  dirty,
  onSave,
}) => {
  const theme = useTheme();
  const isDark = theme?.mode === 'dark';
  const accentColor = theme?.colors?.button_light || '#fdbf00';
  const primaryColor = theme?.colors?.primary_light || '#1a4275';
  const borderCol = isDark ? 'rgba(51, 65, 85, 0.4)' : '#e2e8f0';
  const textPrimary = isDark ? '#f8fafc' : '#1e293b';

  const overCap = selectedIds.length > maxMembers;
  // At least one member must remain: the backend refuses to empty a group,
  // and a group holding only its tutor is useless anyway.
  const canSave = name.trim().length > 0 && selectedIds.length > 0 && !overCap && !saving && dirty;

  return (
    <div className="w-full flex flex-col h-full overflow-hidden relative">
      {/* Header */}
      <div
        className="p-4 flex items-center gap-3.5 border-b shadow-sm transition-colors duration-300"
        style={{ borderColor: borderCol, backgroundColor: isDark ? '#162542' : '#ffffff' }}
      >
        <button
          onClick={onBack}
          className="p-2 rounded-full hover:bg-slate-100/80 dark:hover:bg-slate-800/50 transition-colors"
          style={{ color: accentColor }}
        >
          <ArrowLeft size={22} />
        </button>
        <div className="flex items-center gap-2">
          <Users size={20} style={{ color: accentColor }} />
          <h3 className="font-bold text-xl m-0" style={{ color: textPrimary }}>
            Editar grupo
          </h3>
        </div>
      </div>

      {error && (
        <div className="bg-rose-500/10 text-rose-500 p-2.5 text-base font-medium text-center border-b border-rose-500/20">
          {error}
        </div>
      )}

      {/* Group name */}
      <div className="px-4 pt-3 pb-1">
        <input
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Nombre del grupo..."
          disabled={saving}
          className={`w-full rounded-full px-4 py-2.5 text-lg font-medium focus:outline-none transition-all duration-300 ${
            isDark
              ? 'bg-slate-800/80 text-white placeholder-slate-500 focus:bg-slate-800 focus:ring-1 focus:ring-slate-600'
              : 'bg-slate-100 text-slate-800 placeholder-slate-400 focus:bg-white focus:ring-1 focus:ring-slate-300'
          }`}
        />
      </div>

      {/* Members */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        <RecipientPicker
          students={students}
          selectedIds={selectedIds}
          onToggle={onToggleMember}
          onSelectAll={onSelectAll}
          onClearAll={onClearAll}
          maxRecipients={maxMembers}
          loading={candidatesLoading}
        />
      </div>

      {/* Save button */}
      <div
        className="p-3 border-t transition-colors duration-300"
        style={{ backgroundColor: isDark ? '#111e35' : '#f8fafc', borderColor: borderCol }}
      >
        <button
          type="button"
          disabled={!canSave}
          onClick={onSave}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-lg text-white disabled:opacity-40 transition-colors"
          style={{ backgroundColor: primaryColor }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : <Check size={18} />}
          Guardar cambios
        </button>
      </div>
    </div>
  );
};

export default GroupManager;
